import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import {
  Download,
  Copy,
  CheckCircle,
  Code,
  Key,
  FileJson,
  FileSpreadsheet,
  RefreshCw,
  Eye,
  EyeOff,
  Info,
  Sparkles,
  Calendar,
  Cloud,
  Webhook
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import { useOfferingType } from '@/hooks/useOfferingType';
import { GoogleBucketTab } from './feedExport/GoogleBucketTab';
import { SchemaDownloadButton, WebhookTab } from './feedExport/WebhookTab';
import { matchingExportRequest } from './feedExport/webhookIntegrationGuide';

interface QuestionWheel {
  id: string;
  name: string;
  question_count: number;
  answered_count: number;
  created_at: string;
}

interface Category {
  name: string;
  product_count: number;
}

// Customer-facing export walk validation script — copied verbatim into this tab beneath the
// "Pagination & Response Headers" section. Approved text, checked in at
// pagemind-vault/decisions/2026-09-16-export-walk-validation-script/export-walk-check.sh
// (vault decision 2026-09-16-export-walk-validation-script.md). This constant must stay
// byte-identical to that file — do not rewrite, reformat, or "improve" the script here; any
// change belongs in the vault file first, with this copy updated to match.
const EXPORT_WALK_CHECK_SCRIPT = [
  '#!/usr/bin/env bash',
  '# PageMind enriched-feed export — walk check.',
  '# Walks your export page by page and tells you whether the walk completed.',
  '# Read-only: every request sends markAsFetched=false, so nothing is marked as delivered.',
  '#',
  '#   ./export-walk-check.sh',
  '#',
  '# Requires: curl, and a JSON parser (jq or python3 — almost every machine has one).',
  '',
  'set -u',
  '',
  '# A real JSON parser is REQUIRED. Without one we cannot tell a valid export page from',
  '# a truncated or corrupt one, and a validator that cannot detect corruption is worse',
  '# than no validator at all — it hands you false assurance.',
  '# `-s` (slurp) reads the WHOLE input as one list of documents, so empty input becomes',
  '# [] (length 0, not 1) and trailing junk like `[] [` fails to parse. Without it, jq',
  '# exits 0 and prints nothing on an empty body, and prints a count before failing on',
  '# trailing junk — both of which would read as a successful page.',
  '#',
  '# ⚠️ PYTHON3 IS PREFERRED OVER JQ, and the reason is not style. The point of this check is',
  '# "can the customer\'s consumer read this payload", and the answer has to match a strict JSON',
  '# parser. Measured against JSON.parse on ten bodies:',
  '#',
  '#   body                     jq            python3       JSON.parse',
  '#   [NaN] [Infinity]         accepts       rejects       rejects',
  '#   [{"price":-Infinity}]    accepts       rejects       rejects',
  '#   [01] [1.] [.1] [+1]      accepts       rejects       rejects',
  '#   [1] [1.5] [1e5] [-0.5]   accepts       accepts       accepts',
  '#',
  '# python3 agrees with JSON.parse on every one. jq does not: it normalises numbers as it parses,',
  '# so `[01]`, `[1.]`, `[.1]` and `[+1]` are already valid numbers by the time any check could',
  '# look at them — a non-finite check cannot catch these, because the problem is SYNTAX, and the',
  '# syntax is gone. Reporting RESULT: OK for a body the customer\'s own code rejects is worse than',
  '# not checking at all: it is assurance pointing the wrong way.',
  '#',
  '# So: python3 validates when present. jq is a fallback that still catches structure and the',
  '# NaN/Infinity family, and the run SAYS SO rather than quietly certifying what it cannot check.',
  'JSON_STRICT=""',
  'if command -v python3 >/dev/null 2>&1; then',
  '  JSON_STRICT=yes',
  '  JSON_PARSER="python3"',
  '  json_len() { python3 -c \'import json,sys',
  'def _reject(literal):',
  '    raise ValueError("not JSON: " + literal)',
  'try:',
  '    # Strict on number syntax; parse_constant fires for NaN, Infinity and -Infinity only.',
  '    d = json.loads(sys.stdin.read(), parse_constant=_reject)',
  '    print(len(d) if isinstance(d, list) else -1)',
  'except Exception:',
  '    print(-1)\'; }',
  'elif command -v jq >/dev/null 2>&1; then',
  '  JSON_PARSER="jq"',
  '  json_len() { jq -s \'if length==1 and (.[0]|type)=="array"',
  '                      then (if ([.[0] | .. | numbers | select(isnan or isinfinite)] | length) > 0',
  '                            then -1 else (.[0]|length) end)',
  '                      else -1 end\' 2>/dev/null; }',
  'else',
  '  echo "This script needs python3 or jq to check each page is valid JSON. Install either one and re-run." >&2',
  '  echo "python3 is preferred: it agrees with JSON.parse on number syntax, and jq does not." >&2',
  '  exit 1',
  'fi',
  '',
  '# ── Set these three ───────────────────────────────────────────────────────────',
  'API_KEY="${PAGEMIND_API_KEY:-YOUR_API_KEY}"',
  'SITE_ID="${PAGEMIND_SITE_ID:-YOUR_SITE_ID}"',
  'BASE_URL="${PAGEMIND_BASE_URL:-https://api.pagemind.ai}"',
  '',
  '# Stop after this many pages. Raise it (or set 0 for no limit) for a full walk.',
  'MAX_PAGES="${MAX_PAGES:-10}"',
  'case "$MAX_PAGES" in',
  '  \'\'|*[!0-9]*) echo "MAX_PAGES must be a whole number (0 means no limit); got \'$MAX_PAGES\'" >&2; exit 1 ;;',
  'esac',
  '',
  '# Where to keep the responses. Unset = keep nothing.',
  '#   SAVE_DIR=./pages              keeps only the page that FAILED, plus its headers',
  '#   SAVE_DIR=./pages SAVE_ALL=1   keeps every page — see the size warning below',
  '# Not on by default because these pages are big: with includeProductAEO and',
  '# aeoLanguage=all, three consecutive pages at the default pageSize=50 measured',
  '# 8.9, 10.1 and 10.1 MB, so a few hundred pages runs into gigabytes.',
  'SAVE_DIR="${SAVE_DIR:-}"',
  'SAVE_ALL="${SAVE_ALL:-}"',
  '',
  '# The export options you actually use. Keep markAsFetched=false for a safe test.',
  'QUERY="format=json&aeoStatus=approved&filterByAeoStatus=true&includeProductAEO=true&aeoLanguage=all&markAsFetched=false"',
  '# ──────────────────────────────────────────────────────────────────────────────',
  '',
  '# The walk follows URLs the SERVER supplies, and every request carries your API key.',
  '# So the origin is pinned: a continuation pointing anywhere else is refused rather than',
  '# followed, because following it would hand your credential to that host.',
  '# The authority ends at the first \'/\', \'?\' or \'#\' — not at \'/\' alone. With only \'/\' as the',
  '# terminator, `https://api.pagemind.ai?cursor=c1` captured `api.pagemind.ai?cursor=c1` AS THE',
  '# HOST: the resolver then produced `https://api.pagemind.ai?cursor=c1/` and the origin check',
  '# rejected a perfectly same-origin continuation.',
  'origin_of() { printf \'%s\' "$1" | sed -n \'s|^\\([A-Za-z][A-Za-z0-9+.-]*://[^/?#]*\\).*|\\1|p\'; }',
  '',
  '# Validates the DELIMITER STRUCTURE of a Link header before any attempt to read meaning from it:',
  '# quotes balanced (quoted-pairs honoured), and <target> brackets opened, closed, never nested.',
  '# Sets LINK_BAD_REASON on failure.',
  '#',
  '# Both failures are silent-by-default disasters, and for the same reason: an unterminated',
  '# delimiter swallows the text that would have ended it.',
  '#   rel="next                     the unterminated QUOTE swallows the rest of the line, so the',
  '#                                 relation reads as an unquoted token `"next` and matches nothing.',
  '#   <http://x/prev; rel=prev, ... the unterminated TARGET swallows the COMMA that separates',
  '#                                 link-values, so a later `rel=next` entry is never seen as an',
  '#                                 entry and the first entry\'s `prev` answers for the whole header.',
  '# In both cases a header advertising another page parses as "there is no next page" — the exact',
  '# false completion this script exists to catch. Quotes inside a <target> are not string',
  '# delimiters, so they are not counted.',
  'link_header_wellformed() {',
  '  _ls="$1"; _lq=0; _lt=0; _li=0',
  '  LINK_BAD_REASON=""',
  '  while [ "$_li" -lt "${#_ls}" ]; do',
  '    _lc="${_ls:$_li:1}"',
  '    if [ "$_lq" -eq 1 ]; then',
  '      if [ "$_lc" = "\\\\" ]; then _li=$((_li + 2)); continue; fi   # quoted-pair',
  '      [ "$_lc" = \'"\' ] && _lq=0',
  '      _li=$((_li + 1)); continue',
  '    fi',
  '    case "$_lc" in',
  '      \'"\') [ "$_lt" -eq 0 ] && _lq=1 ;;',
  '      \'<\') if [ "$_lt" -eq 1 ]; then LINK_BAD_REASON="a nested < inside a target"; return 1; fi',
  '           _lt=1 ;;',
  '      \'>\') _lt=0 ;;',
  '    esac',
  '    _li=$((_li + 1))',
  '  done',
  '  [ "$_lq" -eq 1 ] && { LINK_BAD_REASON="an unmatched quote"; return 1; }',
  '  [ "$_lt" -eq 1 ] && { LINK_BAD_REASON="an unterminated < target"; return 1; }',
  '  return 0',
  '}',
  '',
  '# Resolves a continuation reference against the URL it came from, per RFC 3986: absolute and',
  '# scheme-relative references are taken as they are; `/path` resolves against the origin;',
  '# `?query` keeps the current PATH and replaces only the query; anything else resolves against',
  '# the current directory.',
  '#',
  '# Resolving against the BASE ORIGIN alone was wrong for the query-only form, which is the one a',
  '# server is most likely to send: `?cursor=c1` became `<origin>/?cursor=c1`, silently dropping the',
  '# export path and requesting the site root. Resolution happens BEFORE the origin and read-only',
  '# checks, so both still see a complete absolute URL.',
  '# RFC 3986 §5.2.4 remove_dot_segments. Without it the SAME endpoint has unlimited spellings:',
  '# a server answering `./enriched-feed` every time produces `/export/./enriched-feed`, then',
  '# `/export/././enriched-feed`, then `/export/./././enriched-feed` — one endpoint to curl, a new',
  '# string to the loop guard every page, and with MAX_PAGES=0 a walk that never ends.',
  'remove_dot_segments() {',
  '  _in="$1"; _out=""',
  '  while [ -n "$_in" ]; do',
  '    case "$_in" in',
  '      ../*)  _in="${_in#../}" ;;',
  '      ./*)   _in="${_in#./}" ;;',
  '      /./*)  _in="/${_in#/./}" ;;',
  '      /.)    _in="/" ;;',
  '      /../*) _in="/${_in#/../}"; _out="${_out%/*}" ;;',
  '      /..)   _in="/"; _out="${_out%/*}" ;;',
  '      .|..)  _in="" ;;',
  '      /*)    _rest="${_in#/}"',
  '             case "$_rest" in',
  '               */*) _out="$_out/${_rest%%/*}"; _in="/${_rest#*/}" ;;',
  '               *)   _out="$_out/$_rest"; _in="" ;;',
  '             esac ;;',
  '      *)     case "$_in" in',
  '               */*) _out="$_out${_in%%/*}"; _in="/${_in#*/}" ;;',
  '               *)   _out="$_out$_in"; _in="" ;;',
  '             esac ;;',
  '    esac',
  '  done',
  '  printf \'%s\' "$_out"',
  '}',
  '',
  '# Resolves a continuation reference against the URL it came from, per RFC 3986, and NORMALISES',
  '# the result. Every branch ends at the same normalise-and-print, deliberately: five separate',
  '# review findings were all one mistake — comparing the text a server SENT instead of the request',
  '# it WILL SEE — and each was a different spelling reaching the loop guard unnormalised (the whole',
  '# query string, a percent-encoded name, a fragment, a fragment-only reference, and now dot',
  '# segments). Patching spellings one at a time is what kept it alive, so normalisation lives HERE,',
  '# at the single point every continuation passes through, ahead of every reader.',
  'resolve_ref() {',
  '  _rbase="$1"; _rref="$2"',
  '',
  '  # Scheme-relative: make it absolute against the base scheme, then fall through as absolute.',
  '  case "$_rref" in //*) _rref="${_rbase%%://*}:$_rref" ;; esac',
  '',
  '  _rorigin=$(origin_of "$_rref")',
  '  if [ -n "$_rorigin" ]; then                      # already absolute',
  '    _rest="${_rref#"$_rorigin"}"; _rest="${_rest%%\\#*}"',
  '  else',
  '    _rorigin=$(origin_of "$_rbase")',
  '    _bpathq="${_rbase#"$_rorigin"}"; _bpathq="${_bpathq%%\\#*}"',
  '    _bpath="${_bpathq%%\\?*}"',
  '    [ -z "$_bpath" ] && _bpath="/"',
  '    case "$_rref" in',
  '      /*)  _rest="$_rref" ;;',
  '      \\?*) _rest="$_bpath$_rref" ;;',
  '      # A fragment-only reference names a place in the SAME document: base path AND query.',
  '      \\#*) _rest="$_bpathq" ;;',
  '      *)   _rest="${_bpath%/*}/$_rref" ;;',
  '    esac',
  '    _rest="${_rest%%\\#*}"',
  '  fi',
  '',
  '  _rp="${_rest%%\\?*}"',
  '  case "$_rest" in *\\?*) _rq="?${_rest#*\\?}" ;; *) _rq="" ;; esac',
  '  [ -z "$_rp" ] && _rp="/"',
  '  printf \'%s%s%s\' "$_rorigin" "$(remove_dot_segments "$_rp")" "$_rq"',
  '}',
  '',
  '# Percent-decodes one query-parameter NAME. `%6DarkAsFetched` is `markAsFetched` to the server,',
  '# and a literal-string comparison cannot see that.',
  'decode_name() {',
  '  case "$1" in',
  '    *%*) printf \'%b\' "$(printf \'%s\' "$1" | sed \'s/%/\\\\x/g\')" ;;',
  '    *)   printf \'%s\' "$1" ;;',
  '  esac',
  '}',
  '',
  '# THE READ-ONLY POST-CONDITION, asserted on the FINISHED url rather than trusted to the rewrite',
  '# that built it. Returns 0 only when the URL provably carries exactly one markAsFetched',
  '# parameter, set to false, with no fragment.',
  '#',
  '# This exists because construction-time reasoning has now failed three times on this one',
  '# guarantee: a substring rewrite that replaced a decoy and left the real parameter at true; a',
  '# percent-encoded name (`%6DarkAsFetched=true`) that slipped past an exact-name filter; and an',
  '# override appended after a `#`, which the client never sends, leaving the real parameter live.',
  '# Each was a different bug with the same consequence — the walk telling the export to mark a',
  '# customer\'s products as delivered — so the check now happens on the artefact, where all three',
  '# are visible, instead of on the logic, where each was invisible in its turn.',
  'assert_read_only() {',
  '  case "$1" in *\\#*) return 1 ;; esac        # a fragment means our override may be unsent',
  '  case "$1" in *\\?*) _aq="${1#*\\?}" ;; *) return 1 ;; esac',
  '  _an=0; _abad=0',
  '  _aoldifs="${IFS-}"',
  '  IFS=\'&\'',
  '  for _akv in $_aq; do',
  '    [ -z "$_akv" ] && continue',
  '    if [ "$(decode_name "${_akv%%=*}")" = "markAsFetched" ]; then',
  '      _an=$((_an + 1))',
  '      [ "${_akv#*=}" = "false" ] || _abad=1',
  '    fi',
  '  done',
  '  IFS="$_aoldifs"',
  '  [ "$_an" -eq 1 ] && [ "$_abad" -eq 0 ]',
  '}',
  '',
  '# Returns the value of the `cursor` query parameter, by EXACT name and percent-decoded.',
  '#',
  '# The loop guard below compares cursors to notice a server serving the same page forever. It',
  '# used to compare `${next#*cursor=}` — everything after the first `cursor=`, which includes any',
  '# parameters that follow. So `?cursor=SAME&trace=1` and `?cursor=SAME&trace=2` read as two',
  '# different cursors: reproduced, five identical pages walked without the guard firing, stopped',
  '# only by MAX_PAGES. On a real full walk (MAX_PAGES=0) that is an endless loop, which is the one',
  '# thing this guard exists to prevent. Decoding matters for the same reason: one cursor spelled',
  '# two ways must not count as two.',
  'cursor_value() {',
  '  case "$1" in *\\?*) _q="${1#*\\?}" ;; *) return 1 ;; esac',
  '  _found=""',
  '  _oldifs="${IFS-}"',
  '  IFS=\'&\'',
  '  for _kv in $_q; do',
  '    case "$_kv" in *=*) ;; *) continue ;; esac',
  '    # Names are percent-DECODED before comparison: `%63ursor` is `cursor` to the server, and a',
  '    # literal comparison missed it. Missing the cursor then fell back to comparing the whole',
  '    # URL, which a changing `trace=N` defeats — so an uncapped run could loop forever on one',
  '    # server-visible cursor. assert_read_only already decoded names for exactly this reason;',
  '    # this function did not, which is the same bug surviving in the place nobody re-read.',
  '    if [ "$(decode_name "${_kv%%=*}")" = "cursor" ]; then _found="${_kv#*=}"; break; fi',
  '  done',
  '  IFS="$_oldifs"',
  '  [ -z "$_found" ] && return 1',
  '  case "$_found" in',
  '    *%*) _found="${_found//+/ }"; _found=$(printf \'%b\' "${_found//%/\\\\x}") ;;',
  '  esac',
  '  printf \'%s\' "$_found"',
  '}',
  'BASE_ORIGIN=$(origin_of "$BASE_URL")',
  'if [ -z "$BASE_ORIGIN" ]; then',
  '  echo "BASE_URL must be an absolute URL, e.g. https://api.pagemind.ai — got \'$BASE_URL\'" >&2; exit 1',
  'fi',
  '',
  'if [ -n "$SAVE_DIR" ]; then',
  '  mkdir -p "$SAVE_DIR" || { echo "cannot create SAVE_DIR \'$SAVE_DIR\'" >&2; exit 1; }',
  '  if [ -n "$SAVE_ALL" ]; then',
  '    echo "NOTE: SAVE_ALL is on — every page is kept in $SAVE_DIR."',
  '    echo "      Pages of this export have measured ~9-10 MB each, so a full walk can run to gigabytes."',
  '  fi',
  'fi',
  '',
  'URL="$BASE_URL/api/sites/$SITE_ID/categories/all/export/enriched-feed?$QUERY"',
  'HDR=$(mktemp); BODY=$(mktemp); trap \'rm -f "$HDR" "$BODY"\' EXIT',
  '',
  '# Keep the response for a page. Zero-padded so files sort in walk order, and the',
  '# headers are kept beside the body because the pagination evidence lives there.',
  'saved_this_page=""',
  'save_page() {',
  '  [ -z "$SAVE_DIR" ] && return 0',
  '  [ -n "$saved_this_page" ] && return 0      # also blocks a retry after a failed save',
  '  _n=$(printf \'%04d\' "$page")',
  '  _b="$SAVE_DIR/${SITE_ID}_page${_n}.json"',
  '  _h="$SAVE_DIR/${SITE_ID}_page${_n}.headers.txt"',
  '  # Both copies are checked, and success is claimed only once both files exist. The',
  '  # previous version sent cp\'s stderr to /dev/null and ignored its exit status, so a',
  '  # save into an unwritable directory printed "saved" and the run still ended RESULT: OK',
  '  # with an empty directory — a fabricated success, and the worst kind, because the',
  '  # files it claims to have written are the evidence you would go looking for.',
  '  if cp "$BODY" "$_b" && cp "$HDR" "$_h" && [ -f "$_b" ] && [ -f "$_h" ]; then',
  '    saved_this_page=yes',
  '    echo "    saved $_b (and .headers.txt)"',
  '  else',
  '    saved_this_page=failed',
  '    echo "    ERROR: could not save the response into \'$SAVE_DIR\' — check the path and permissions" >&2',
  '    errors=$((errors+1))',
  '  fi',
  '}',
  '',
  'page=0; total=""; total_seen=""; total_vanished=""; page_seen=""; aborted=""; rows_sum=0; errors=0; warnings=0; finished=""',
  'declare -a seen_cursors=()',
  '',
  'printf \'%s\\n\' "PageMind export walk check — $(date)"',
  'printf \'%s\\n\' "base: $BASE_URL"',
  'printf \'%s\\n\' "site: $SITE_ID"',
  'printf \'%s\\n\' "read-only: markAsFetched=false on every request"',
  'printf \'%s\\n\\n\' "JSON checked with: $JSON_PARSER$([ -n "$JSON_STRICT" ] && echo \' (strict — matches JSON.parse)\' || echo \' (NOT strict on number syntax — see the note at the end)\')"',
  '',
  'while :; do',
  '  page=$((page + 1))',
  '',
  '  # The baseline is taken BEFORE the request, not after the header parse. Anything that',
  '  # can raise an error on this page — curl itself, a malformed Link, the cursor guard,',
  '  # the origin refusal — must be inside the window the failure-save is measured against.',
  '  # When it was captured later, a malformed Link header exited 1 with an empty SAVE_DIR:',
  '  # the run failed and kept nothing, which is the one moment the files are worth having.',
  '  saved_this_page=""',
  '  errors_before=$errors',
  '',
  '  code=$(curl -sS -m 300 -D "$HDR" -o "$BODY" -w \'%{http_code}\' \\',
  '              -H "Authorization: Bearer $API_KEY" "$URL") \\',
  '    || { echo "  page $page: curl failed"; errors=$((errors+1)); save_page; break; }',
  '',
  '  hdr_page=$(grep -i \'^x-pagemind-page:\'        "$HDR" | tr -d \'\\r\' | sed \'s/.*: *//\')',
  '  hdr_total=$(grep -i \'^x-pagemind-total-pages:\' "$HDR" | tr -d \'\\r\' | sed \'s/.*: *//\')',
  '  # Link parsing. Three outcomes must stay distinct, because conflating the last two',
  '  # is how a broken walk reads as a finished one:',
  '  #   no Link header at all              -> end of walk',
  '  #   Link header with no next relation  -> end of walk',
  '  #   Link header advertising next but unreadable -> ERROR, not end of walk',
  '  # `rel=next` without quotes is legal per RFC 8288, so both spellings are accepted.',
  '  link_raw=$(grep -i \'^link:\' "$HDR" | tr -d \'\\r\')',
  '  next=""',
  '  if [ -n "$link_raw" ] && ! link_header_wellformed "$link_raw"; then',
  '    echo "    ERROR: the Link header has $LINK_BAD_REASON, so it cannot be parsed reliably: $link_raw"',
  '    echo "           Not treating this as the end of the walk — a header we cannot parse is a"',
  '    echo "           failure to read it, which is not the same as there being no next page."',
  '    errors=$((errors+1))',
  '    link_raw=""                                      # do not parse a header we do not trust',
  '  fi',
  '  if [ -n "$link_raw" ]; then',
  '    # The RELATION is parsed INDEPENDENTLY of the target, and that separation is the',
  '    # whole point. Deciding "is a next page advertised?" from whether a <URL> could be',
  '    # extracted conflates two different questions, and every spelling that answers yes',
  '    # to the first and no to the second then reads as a finished walk. `Link: ; rel=next`',
  '    # — advertised, no target — is exactly that case, and it slipped through the previous',
  '    # version with COMPLETE and exit 0.',
  '    #',
  '    # The parameters are PARSED, not pattern-matched. Searching the header text for `rel=`',
  '    # is wrong in both directions and both were shipped: `title="rel=prev"; rel=next` found',
  '    # the decoy inside the quoted title and skipped a real page, and `REL=next` was missed',
  '    # because the regex was lower-case. So: split parameters on `;` OUTSIDE quotes, split',
  '    # each on its first `=` OUTSIDE quotes, compare the whole parameter name',
  '    # case-insensitively, and unquote the value.',
  '    #',
  '    # RFC 8288 §3.3 on the value: a QUOTED value may hold a space-separated list of',
  '    # relations (`rel="prev next"`), while an UNQUOTED one is a single relation-type token —',
  '    # so a trailing word after an unquoted value is not a relation and must not count.',
  '    # Output is `<advertised>\\t<url>`; a tab cannot occur in either field.',
  '    # Link-values are split on commas OUTSIDE quoted strings and OUTSIDE <...> targets, by the',
  '    # awk below rather than by sed. The old sed split on `,[[:space:]]*<` — a comma only counted',
  '    # when the NEXT entry began with a target, which made a targetless entry invisible as an',
  '    # entry at all: `<...>; rel=prev, ; rel=next` never split, parsed as ONE link-value whose',
  '    # first rel parameter was `prev,`, and the rel=next was never looked at. Reported complete',
  '    # for a header advertising a second page. The bug was in the split, one level above the',
  '    # parameter parsing, which is why fixing the parameters did not reach it.',
  '    _parsed=$(printf \'%s\' "$link_raw" \\',
  '      | sed \'s/^[Ll][Ii][Nn][Kk]:[[:space:]]*//\' \\',
  '      | awk \'',
  '          # Removes RFC 7230 quoted-pair escapes from an already-unquoted value.',
  '          function unescape_qs(v,   i, c, out) {',
  '            out = ""',
  '            for (i = 1; i <= length(v); i++) {',
  '              c = substr(v, i, 1)',
  '              if (c == "\\\\" && i < length(v)) { i++; out = out substr(v, i, 1); continue }',
  '              out = out c',
  '            }',
  '            return out',
  '          }',
  '          # Returns the rel parameter value of one link-value, or "" if it has none.',
  '          # A backslash inside a quoted string escapes the NEXT character (RFC 7230',
  '          # quoted-pair), so an escaped quote does NOT end the string. Toggling on every',
  '          # quote desynchronises the parse from that point on and misreads every later',
  '          # parameter: title="a\\"b"; rel=next read as having no next relation at all and',
  '          # reported COMPLETE after one page.',
  '          function rel_value(s,   i, c, inq, cur, np, params, j, p, eq, name, val) {',
  '            np = 0; cur = ""; inq = 0',
  '            for (i = 1; i <= length(s); i++) {',
  '              c = substr(s, i, 1)',
  '              if (inq && c == "\\\\" && i < length(s)) { cur = cur c substr(s, i + 1, 1); i++; continue }',
  '              if (c == "\\"") { inq = !inq; cur = cur c; continue }',
  '              if (c == ";" && !inq) { params[++np] = cur; cur = ""; continue }',
  '              cur = cur c',
  '            }',
  '            params[++np] = cur',
  '            for (j = 1; j <= np; j++) {',
  '              p = params[j]; eq = 0; inq = 0',
  '              for (i = 1; i <= length(p); i++) {',
  '                c = substr(p, i, 1)',
  '                if (inq && c == "\\\\" && i < length(p)) { i++; continue }',
  '                if (c == "\\"") { inq = !inq; continue }',
  '                if (c == "=" && !inq) { eq = i; break }',
  '              }',
  '              if (eq == 0) continue',
  '              name = substr(p, 1, eq - 1); val = substr(p, eq + 1)',
  '              gsub(/^[[:space:]]+/, "", name); gsub(/[[:space:]]+$/, "", name)',
  '              gsub(/^[[:space:]]+/, "", val);  gsub(/[[:space:]]+$/, "", val)',
  '              if (tolower(name) != "rel") continue',
  '              if (val ~ /^".*"$/) return unescape_qs(substr(val, 2, length(val) - 2))',
  '              sub(/[[:space:]].*$/, "", val)          # unquoted = one token',
  '              return val',
  '            }',
  '            return ""',
  '          }',
  '          # Splits the header into link-values on commas outside quotes and outside <...>.',
  '          function split_values(s, out,   i, c, inq, intgt, cur, n) {',
  '            n = 0; cur = ""; inq = 0; intgt = 0',
  '            for (i = 1; i <= length(s); i++) {',
  '              c = substr(s, i, 1)',
  '              if (inq) {',
  '                if (c == "\\\\" && i < length(s)) { cur = cur c substr(s, i + 1, 1); i++; continue }',
  '                if (c == "\\"") inq = 0',
  '                cur = cur c; continue',
  '              }',
  '              if (intgt) { if (c == ">") intgt = 0; cur = cur c; continue }',
  '              if (c == "\\"") { inq = 1; cur = cur c; continue }',
  '              if (c == "<")  { intgt = 1; cur = cur c; continue }',
  '              if (c == ",")  { out[++n] = cur; cur = ""; continue }',
  '              cur = cur c',
  '            }',
  '            out[++n] = cur',
  '            return n',
  '          }',
  '          # EVERY link-value is examined, and the two answers are independent: `found` is the',
  '          # first readable next target, `bad` records that SOME next entry was unreadable. The',
  '          # earlier version stopped at the first next-advertising entry, so a readable next and',
  '          # a broken entry in the same header lost whichever came second — and a broken entry',
  '          # after a readable one was silently ignored.',
  '          {',
  '            nv = split_values($0, vals)',
  '            for (v = 1; v <= nv; v++) {',
  '              line = vals[v]',
  '              # The target is recognised ONLY at the START of a link-value, which is where RFC',
  '              # 8288 puts it: link-value = "<" URI-Reference ">" *( ";" link-param ). Searching',
  '              # anywhere for <...> picked up angle brackets inside a quoted PARAMETER instead:',
  '              # `; title="<ignored>"; rel=next` yielded <ignored> as the target and then lost',
  '              # the relation entirely (the remainder began mid-quote, so the rel parameter was',
  '              # read as quoted text), reporting COMPLETE with a next page advertised.',
  '              target = ""; rest = line',
  '              sub(/^[[:space:]]+/, "", rest)',
  '              if (substr(rest, 1, 1) == "<") {',
  '                gt = index(rest, ">")',
  '                # No closing ">" is a malformed target: leave it unset, so an entry advertising',
  '                # next is flagged rather than followed to something guessed out of the text.',
  '                if (gt > 0) { target = substr(rest, 2, gt - 2); rest = substr(rest, gt + 1) }',
  '              }',
  '              rel = rel_value(rest)',
  '              isnext = 0',
  '              m = split(rel, tok, /[[:space:]]+/)',
  '              for (i = 1; i <= m; i++) if (tolower(tok[i]) == "next") isnext = 1',
  '              if (!isnext) continue',
  '              adv = 1',
  '              if (target != "") { if (found == "") found = target }',
  '              else bad = 1',
  '            }',
  '          }',
  '          END { printf "%d\\t%d\\t%s\\n", adv + 0, bad + 0, found }',
  '        \')',
  '    # fields: <any next advertised>  <some next entry unreadable>  <first readable next target>',
  '    _advertised="${_parsed%%	*}"',
  '    _rest="${_parsed#*	}"',
  '    _unreadable="${_rest%%	*}"',
  '    next="${_rest#*	}"',
  '    # Reported whenever ANY next entry was unreadable, even if another entry gave a usable URL.',
  '    # Tying this to an empty `next` hid a broken entry that sat beside a working one.',
  '    if [ "$_unreadable" = "1" ] || { [ "$_advertised" = "1" ] && [ -z "$next" ]; }; then',
  '      echo "    ERROR: a next page is advertised in the Link header but its URL could not be read: $link_raw"',
  '      errors=$((errors+1))',
  '    fi',
  '  fi',
  '',
  '  # RESOLVED AND FRAGMENT-STRIPPED HERE, before anything else reads `next` — the cursor guard',
  '  # included. A fragment is client-side only and never reaches the server, so `?cursor=SAME#1`',
  '  # and `?cursor=SAME#2` are the SAME request; comparing them unresolved made them look like',
  '  # two different cursors, and the guard never fired on identical server-visible pages. Same',
  '  # mistake as comparing the whole query instead of the cursor value: compare what the SERVER',
  '  # sees, not the text it happened to send.',
  '  if [ -n "$next" ]; then',
  '    next=$(resolve_ref "$URL" "$next")',
  '    next="${next%%\\#*}"',
  '  fi',
  '',
  '  [ -n "$SAVE_ALL" ] && save_page',
  '',
  '  # Parse the body. Accept ONLY a clean non-negative integer: anything else — empty',
  '  # output, "-1", or multi-line output from a parser that printed then failed — means',
  '  # the body was not exactly one JSON array. Validated here so no parser quirk can',
  '  # turn a broken page into a successful one.',
  '  rows=$(json_len < "$BODY")',
  '  case "$rows" in',
  '    \'\'|*[!0-9]*)',
  '      echo "  page $page: ERROR — response body is not exactly one JSON array (empty, truncated or corrupt)"',
  '      errors=$((errors+1)); save_page; break ;;',
  '  esac',
  '  rows_sum=$((rows_sum + rows))',
  '',
  '  # ── report this page ──',
  '  label="page ${hdr_page:-$page}"',
  '  [ -n "$hdr_total" ] && label="$label of $hdr_total"',
  '  printf \'  %-22s HTTP %s   %4s products   %s\\n\' "$label" "$code" "$rows" \\',
  '         "$([ -n "$next" ] && echo \'next page ->\' || echo \'END (no next link)\')"',
  '',
  '  # ── checks ──',
  '  [ "$code" != "200" ] && { echo "    ERROR: expected HTTP 200"; errors=$((errors+1)); save_page; break; }',
  '',
  '  if [ -n "$hdr_page" ]; then',
  '    page_seen=yes',
  '    if [ "$hdr_page" != "$page" ]; then',
  '      echo "    ERROR: X-PageMind-Page is $hdr_page, expected $page"; errors=$((errors+1))',
  '    fi',
  '  elif [ -n "$page_seen" ]; then',
  '    # Reported on an earlier page, so it must keep being reported (same rule as the total).',
  '    echo "    ERROR: X-PageMind-Page was reported earlier and is missing on this page"',
  '    errors=$((errors+1))',
  '  fi',
  '  if [ -n "$hdr_total" ]; then',
  '    total_seen=yes',
  '    if [ -z "$total" ]; then total="$hdr_total"',
  '    elif [ "$hdr_total" != "$total" ]; then',
  '      echo "    ERROR: X-PageMind-Total-Pages changed mid-walk ($total -> $hdr_total)"; errors=$((errors+1))',
  '    fi',
  '  elif [ -n "$total_seen" ]; then',
  '    # It was reported earlier in this walk, so it must keep being reported.',
  '    echo "    ERROR: X-PageMind-Total-Pages was $total earlier and is missing on this page"',
  '    errors=$((errors+1)); total_vanished=yes',
  '  fi',
  '',
  '  # Any error on this page? Keep it, so there is something to look at afterwards.',
  '  [ "$errors" -gt "$errors_before" ] && save_page',
  '',
  '  # cursor loop guard',
  '  if [ -n "$next" ]; then',
  '    # The cursor VALUE alone. With no cursor parameter at all, fall back to the whole URL so a',
  '    # cursorless pagination scheme still gets loop detection rather than none.',
  '    cur=$(cursor_value "$next")',
  '    [ -z "$cur" ] && cur="$next"',
  '    for s in ${seen_cursors[@]+"${seen_cursors[@]}"}; do',
  '      [ "$s" = "$cur" ] && { echo "    ERROR: cursor repeated — aborting to avoid an endless loop"; errors=$((errors+1)); save_page; aborted=yes; next=""; break; }',
  '    done',
  '    seen_cursors+=("$cur")',
  '  fi',
  '',
  '  # An abort is NOT a finished walk — only an absent next link on a healthy page is.',
  '  if [ -z "$next" ]; then',
  '    [ -z "$aborted" ] && finished=yes',
  '    break',
  '  fi',
  '  if [ "$MAX_PAGES" != "0" ] && [ "$page" -ge "$MAX_PAGES" ]; then',
  '    echo "    stopped at MAX_PAGES=$MAX_PAGES — the walk did NOT finish. Raise MAX_PAGES (or set 0) for a full run."',
  '    warnings=$((warnings+1)); break',
  '  fi',
  '  # A relative continuation is resolved against the base; an absolute one must match',
  '  # the base origin exactly (scheme, host and port) or we refuse to follow it.',
  '  next_origin=$(origin_of "$next")',
  '  if [ -z "$next_origin" ]; then',
  '    echo "    ERROR: could not resolve the next-page link into an absolute URL: $next"',
  '    errors=$((errors+1)); save_page; aborted=yes; break',
  '  elif [ "$next_origin" != "$BASE_ORIGIN" ]; then',
  '    echo "    ERROR: the next-page link points at $next_origin but you are testing $BASE_ORIGIN."',
  '    echo "           Refusing to send your API key to a different origin."',
  '    errors=$((errors+1)); save_page; aborted=yes; break',
  '  fi',
  '',
  '  # Follow the server\'s next-page URL, but re-assert the read-only flag ourselves.',
  '  # The server does carry it forward today; this way the guarantee is the script\'s,',
  '  # not something we are trusting the server to keep doing.',
  '  #',
  '  # Rebuild the query by EXACT parameter name. A substring rewrite is not enough:',
  '  # `notmarkAsFetched=false&markAsFetched=true` would have had the decoy rewritten',
  '  # and the real parameter left at `true`, which tells the export to mark your',
  '  # products as delivered — the exact opposite of this script\'s promise.',
  '  # The FRAGMENT goes first. It is client-side only and never reaches the server, so appending',
  '  # the override after a `#` left it inert while the real markAsFetched=true rode along in the',
  '  # query. Reported and reproduced as `?%6DarkAsFetched=true&cursor=c1#section`.',
  '',
  '  _path="${next%%\\?*}"',
  '  _query=""',
  '  case "$next" in *\\?*) _query="${next#*\\?}" ;; esac',
  '  _rebuilt=""',
  '  if [ -n "$_query" ]; then',
  '    _oldifs="${IFS-}"',
  '    IFS=\'&\'',
  '    for _kv in $_query; do',
  '      [ -z "$_kv" ] && continue',
  '      # Names are percent-DECODED before comparison, so an encoded spelling cannot survive.',
  '      case "$(decode_name "${_kv%%=*}")" in',
  '        markAsFetched) ;;                                       # drop EVERY spelling of it',
  '        *) _rebuilt="${_rebuilt:+$_rebuilt&}$_kv" ;;',
  '      esac',
  '    done',
  '    IFS="$_oldifs"',
  '  fi',
  '  URL="$_path?${_rebuilt:+$_rebuilt&}markAsFetched=false"',
  '',
  '  # Assert it, do not assume it. If the finished URL cannot be shown to be read-only, the walk',
  '  # stops here: continuing would risk changing the customer\'s delivery state with their own key,',
  '  # which is the one thing this script promises it will never do.',
  '  if ! assert_read_only "$URL"; then',
  '    echo "    ERROR: refusing to continue — markAsFetched=false could not be guaranteed on the next request"',
  '    echo "           next URL was: $URL"',
  '    errors=$((errors+1)); save_page; aborted=yes; break',
  '  fi',
  'done',
  '',
  'printf \'\\n%s\\n\' "-----------------------------------------------------------"',
  'printf \'%s\\n\' "pages walked : $page"',
  'printf \'%s\\n\' "products seen: $rows_sum"',
  '',
  '# One rule, not four special cases: the walk is only reported as clean when it both',
  '# reached the end AND raised no errors. Every previous version of this block claimed',
  '# COMPLETE next to an error at least once.',
  'if [ -n "$finished" ] && [ "$errors" -eq 0 ]; then',
  '  printf \'%s\\n\' "walk         : COMPLETE — reached a page with no next link"',
  'elif [ -n "$finished" ]; then',
  '  printf \'%s\\n\' "walk         : reached the end, but errors were found — see above. Do not treat this as a good export."',
  'else',
  '  printf \'%s\\n\' "walk         : INCOMPLETE — stopped before the end (see above)"',
  'fi',
  '',
  'if [ -n "$total_vanished" ]; then',
  '  printf \'%s\\n\' "total pages  : reported $total at first, then stopped being reported — see the error above"',
  'elif [ -z "$total_seen" ]; then',
  '  printf \'%s\\n\' "total pages  : not reported for this request"',
  '  printf \'%s\\n\' "               Two reasons this is normal: (a) the request uses pendingRepublish,"',
  '  printf \'%s\\n\' "               pendingDelivery or changed_since, where the total is too slow to count;"',
  '  printf \'%s\\n\' "               (b) the server predates the pagination headers. Neither is an error."',
  '  warnings=$((warnings+1))',
  'elif [ -z "$finished" ]; then',
  '  printf \'%s\\n\' "total pages  : reported $total — not comparable, this walk was cut short"',
  'elif [ "$page" != "$total" ]; then',
  '  printf \'%s\\n\' "total pages  : reported $total, walk completed on page $page"',
  '  printf \'%s\\n\' "               NOTE: not an error. The total is counted when the walk starts, and the"',
  '  printf \'%s\\n\' "               catalogue can change while you page through it. The authoritative end of"',
  '  printf \'%s\\n\' "               the walk is the page with no next link — which you reached."',
  '  warnings=$((warnings+1))',
  'else',
  '  printf \'%s\\n\' "total pages  : reported $total, walk completed on page $page — match"',
  'fi',
  '',
  '# Said out loud rather than left implicit: on the jq-only path this run cannot certify that the',
  '# payload is acceptable to a strict JSON parser, because jq accepts number forms JSON.parse',
  '# rejects. The walk result above is unaffected; the JSON claim is the narrower one.',
  'if [ -z "$JSON_STRICT" ]; then',
  '  printf \'%s\\n\' "json check    : NOT fully verified — jq was used, and jq accepts number forms that"',
  '  printf \'%s\\n\' "                JSON.parse rejects (01, 1., .1, +1), so a page carrying one would"',
  '  printf \'%s\\n\' "                have passed here and still broken your consumer. Structure and the"',
  '  printf \'%s\\n\' "                NaN/Infinity family WERE checked. Install python3 and re-run for a"',
  '  printf \'%s\\n\' "                strict check."',
  '  warnings=$((warnings+1))',
  'fi',
  '',
  'printf \'\\n\'',
  'if [ "$errors" -gt 0 ]; then',
  '  printf \'%s\\n\' "RESULT: $errors error(s), $warnings warning(s) — something is wrong, send us this output."',
  '  exit 1',
  'fi',
  'printf \'%s\\n\' "RESULT: OK — $warnings warning(s), no errors."',
  'exit 0'
].join('\n') + '\n';

function ServiceFeedNotice() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Feed Export</h1>
        <p className="text-muted-foreground">
          Export enriched product feeds with Q&amp;A data, benefit scores, and persona matching.
        </p>
      </div>
      <Alert>
        <Calendar className="h-4 w-4" />
        <AlertDescription>
          Feed export is available for product sites. Service sites use booking system integrations instead.
        </AlertDescription>
      </Alert>
    </div>
  );
}

export default function FeedExport() {
  const { selectedSite } = useSite();
  const offeringType = useOfferingType();
  const [categories, setCategories] = useState<Category[]>([]);
  const [questionWheels, setQuestionWheels] = useState<QuestionWheel[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedWheel, setSelectedWheel] = useState<string>('');
  const [format, setFormat] = useState<'csv' | 'json'>('csv');
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);

  // AEO Export Options
  const [includeCategoryAEO, setIncludeCategoryAEO] = useState(true);
  const [includeProductAEO, setIncludeProductAEO] = useState(false);
  const [includeAEOQuestions, setIncludeAEOQuestions] = useState(true);
  const [aeoStatus, setAeoStatus] = useState<'approved' | 'all'>('approved');
  const [includeReviewerInfo, setIncludeReviewerInfo] = useState(true);

  // Change Detection Options (section versioning filters)
  const [changedSince, setChangedSince] = useState('');
  const [changedSection, setChangedSection] = useState('');
  const [includeSectionVersions, setIncludeSectionVersions] = useState(false);
  const [hashesOnly, setHashesOnly] = useState(false);
  const [showChangeDetection, setShowChangeDetection] = useState(false);

  // Load categories and question wheels when site changes
  useEffect(() => {
    if (selectedSite?.id) {
      loadCategories();
      loadQuestionWheels();
    }
  }, [selectedSite?.id]);

  // Load API key
  useEffect(() => {
    loadApiKey();
  }, []);

  const loadApiKey = async () => {
    try {
      const response = await authFetch('/api/users/me/api-key');
      if (response.ok) {
        const data = await response.json();
        setApiKey(data.apiKey);
      }
    } catch (error) {
      console.error('Error loading API key:', error);
    }
  };

  const generateApiKey = async () => {
    setIsGeneratingKey(true);
    try {
      const response = await authFetch('/api/users/me/api-key', {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        setApiKey(data.apiKey);
      }
    } catch (error) {
      console.error('Error generating API key:', error);
    } finally {
      setIsGeneratingKey(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await authFetch(`/api/sites/${selectedSite?.id}/product-categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadQuestionWheels = async () => {
    try {
      const response = await authFetch(`/api/sites/${selectedSite?.id}/question-wheels`);
      if (response.ok) {
        const data = await response.json();
        // Response is an array directly, not { wheels: [...] }
        const wheels = Array.isArray(data) ? data : (data.wheels || []);
        // Map to expected interface
        setQuestionWheels(wheels.map((w: any) => ({
          id: w.id,
          name: w.center_term || 'Unnamed Wheel',
          question_count: w.total_questions || 0,
          answered_count: w.answered_count || 0,
          created_at: w.created_at
        })));
      }
    } catch (error) {
      console.error('Error loading question wheels:', error);
    }
  };

  // Offered as a FILE download rather than a paste-the-whole-thing block: the script is
  // ~240 lines, and pasting that into a shell is both unpleasant and easy to truncate.
  // The blob is built from the same constant the source view renders, so there is only
  // ever one copy of the script.
  const downloadWalkCheckScript = () => {
    const blob = new Blob([EXPORT_WALK_CHECK_SCRIPT], { type: 'text/x-shellscript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'export-walk-check.sh';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // ONE COPY BUTTON PER EXAMPLE. They used to be a single block behind a single button, so
  // "copy" on the safe first run also handed the reader two uncapped full-feed walks — paste it
  // and you run one page and then download the whole catalogue twice. A copy button should copy
  // exactly the thing it is next to.
  const walkCheckExamples = [
    {
      id: 'walk-usage-first',
      label: 'Start here — one page',
      note: 'Pages are big: 8.9\u201310.1 MB each with includeProductAEO and aeoLanguage=all. Read the output of one page before downloading more. Without MAX_PAGES the script walks up to 10 pages, about 100 MB on that shape.',
      command: [
        'chmod +x export-walk-check.sh',
        '',
        `PAGEMIND_API_KEY=${apiKey || 'YOUR_API_KEY'} \\`,
        `PAGEMIND_SITE_ID=${selectedSite?.id || 'YOUR_SITE_ID'} \\`,
        '  MAX_PAGES=1 ./export-walk-check.sh',
      ].join('\n'),
    },
    {
      id: 'walk-usage-full',
      label: 'The whole feed',
      note: 'However many pages that takes. Gigabytes on the shape above \u2014 check you have room first.',
      command: [
        `PAGEMIND_API_KEY=${apiKey || 'YOUR_API_KEY'} \\`,
        `PAGEMIND_SITE_ID=${selectedSite?.id || 'YOUR_SITE_ID'} \\`,
        '  MAX_PAGES=0 ./export-walk-check.sh',
      ].join('\n'),
    },
    {
      id: 'walk-usage-save',
      label: 'Something failed and you want the evidence',
      note: 'Keeps only the page that failed, with its headers. That is the file to send us \u2014 our own logs record what we sent and how many rows, never the rows themselves.',
      command: [
        `PAGEMIND_API_KEY=${apiKey || 'YOUR_API_KEY'} \\`,
        `PAGEMIND_SITE_ID=${selectedSite?.id || 'YOUR_SITE_ID'} \\`,
        '  SAVE_DIR=./pages MAX_PAGES=0 ./export-walk-check.sh',
      ].join('\n'),
    },
  ];

  const handleExportByCategory = async () => {
    if (!selectedCategory || !selectedSite?.id) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        format,
        includeEnrichments: 'true',
        includeProductQuestions: 'true',
        // AEO Content Options
        includeCategoryAEO: includeCategoryAEO.toString(),
        includeProductAEO: includeProductAEO.toString(),
        includeAEOQuestions: includeAEOQuestions.toString(),
        aeoStatus: aeoStatus === 'all' ? 'any' : 'approved',
        includeReviewerInfo: includeReviewerInfo.toString()
      });

      // Change Detection params (section versioning)
      if (changedSince) params.set('changed_since', new Date(changedSince).toISOString());
      if (changedSection && changedSection !== '_all') params.set('changed_section', changedSection);
      if (includeSectionVersions) params.set('include_section_versions', 'true');
      if (hashesOnly) params.set('hashes_only', 'true');

      const response = await authFetch(
        `/api/sites/${selectedSite.id}/categories/${encodeURIComponent(selectedCategory)}/export/enriched-feed?${params}`
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `enriched-feed-${selectedCategory.toLowerCase().replace(/\s+/g, '-')}.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Export failed:', await response.text());
      }
    } catch (error) {
      console.error('Error exporting feed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportByWheel = async () => {
    if (!selectedWheel || !selectedSite?.id) return;

    setLoading(true);
    try {
      const response = await authFetch(
        `/api/sites/${selectedSite.id}/question-wheels/${selectedWheel}/export-feed`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            format,
            includeStatus: ['approved', 'pending']
          })
        }
      );

      if (response.ok) {
        if (format === 'csv') {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `enriched-feed-wheel-${selectedWheel}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        } else {
          const data = await response.json();
          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `enriched-feed-wheel-${selectedWheel}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }
      } else {
        console.error('Export failed:', await response.text());
      }
    } catch (error) {
      console.error('Error exporting feed:', error);
    } finally {
      setLoading(false);
    }
  };

  const baseUrl = window.location.origin.replace('3000', '3001');

  if (offeringType === 'service') {
    return <ServiceFeedNotice />;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Feed Export</h1>
        <p className="text-muted-foreground">
          Export enriched product feeds with Q&A data, benefit scores, and persona matching.
        </p>
      </div>

      {offeringType === 'mixed' && (
        <Alert>
          <Calendar className="h-4 w-4" />
          <AlertDescription>
            This site has both products and services. The feed export below covers products.
            Service sites use booking system integrations instead.
          </AlertDescription>
        </Alert>
      )}

      {!selectedSite && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Please select a site from the header to view export options.
          </AlertDescription>
        </Alert>
      )}

      {selectedSite && (
        <Tabs defaultValue="api" className="space-y-4">
          <TabsList>
            <TabsTrigger value="api" className="flex items-center gap-2">
              <Code className="h-4 w-4" />
              API Documentation
            </TabsTrigger>
            <TabsTrigger value="bucket" className="flex items-center gap-2">
              <Cloud className="h-4 w-4" />
              Google bucket
            </TabsTrigger>
            <TabsTrigger value="webhook" className="flex items-center gap-2">
              <Webhook className="h-4 w-4" />
              Webhook
            </TabsTrigger>
            <TabsTrigger value="manual" className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Manual Export
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bucket" className="space-y-4">
            {/* keyed by store (PR #7228 r4): switching stores mounts a fresh tab — no draft, edit state or late answer carries over */}
            <GoogleBucketTab key={selectedSite.id} siteId={selectedSite.id} />
          </TabsContent>

          <TabsContent value="webhook" className="space-y-4">
            {/* keyed by store, as the bucket tab (PR #7228 r4) */}
            <WebhookTab key={selectedSite.id} siteId={selectedSite.id} storeName={selectedSite.name} />
          </TabsContent>

          <TabsContent value="manual" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Export by Category */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileSpreadsheet className="h-5 w-5" />
                    Export by Category
                  </CardTitle>
                  <CardDescription>
                    Export all products in a category with their enrichments
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.name} value={cat.name}>
                            {cat.name} ({cat.product_count} products)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Format</Label>
                    <Select value={format} onValueChange={(v) => setFormat(v as 'csv' | 'json')}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="csv">
                          <div className="flex items-center gap-2">
                            <FileSpreadsheet className="h-4 w-4" />
                            CSV (Spreadsheet)
                          </div>
                        </SelectItem>
                        <SelectItem value="json">
                          <div className="flex items-center gap-2">
                            <FileJson className="h-4 w-4" />
                            JSON (Structured)
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator className="my-4" />

                  {/* AEO Content Options */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-purple-500" />
                      AEO Content Options
                    </Label>

                    <div className="space-y-2 pl-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="includeCategoryAEO"
                          checked={includeCategoryAEO}
                          onCheckedChange={(checked) => setIncludeCategoryAEO(checked as boolean)}
                        />
                        <label htmlFor="includeCategoryAEO" className="text-sm cursor-pointer">
                          Category AEO (featured snippets, buyer guides)
                        </label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="includeAEOQuestions"
                          checked={includeAEOQuestions}
                          onCheckedChange={(checked) => setIncludeAEOQuestions(checked as boolean)}
                        />
                        <label htmlFor="includeAEOQuestions" className="text-sm cursor-pointer">
                          AEO FAQ Questions & Answers
                        </label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="includeProductAEO"
                          checked={includeProductAEO}
                          onCheckedChange={(checked) => setIncludeProductAEO(checked as boolean)}
                        />
                        <label htmlFor="includeProductAEO" className="text-sm cursor-pointer">
                          Product AEO (why-buy, differentiators)
                        </label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="includeReviewerInfo"
                          checked={includeReviewerInfo}
                          onCheckedChange={(checked) => setIncludeReviewerInfo(checked as boolean)}
                        />
                        <label htmlFor="includeReviewerInfo" className="text-sm cursor-pointer">
                          Include reviewer info (for citations)
                        </label>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm">Content Status Filter</Label>
                      <Select value={aeoStatus} onValueChange={(v) => setAeoStatus(v as 'approved' | 'all')}>
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="approved">Approved content only</SelectItem>
                          <SelectItem value="all">All generated content</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Separator className="my-4" />

                  {/* Change Detection (Section Versioning) */}
                  <div className="space-y-3">
                    <button
                      type="button"
                      className="flex items-center gap-2 text-sm font-medium cursor-pointer"
                      onClick={() => setShowChangeDetection(!showChangeDetection)}
                    >
                      <Calendar className="h-4 w-4 text-blue-500" />
                      Change Detection
                      <Badge variant="outline" className="ml-1 text-xs">Versioning filters</Badge>
                      <span className="text-muted-foreground text-xs ml-auto">
                        {showChangeDetection ? '▼' : '▶'}
                      </span>
                    </button>

                    {showChangeDetection && (
                      <div className="space-y-3 pl-2 border-l-2 border-blue-200 ml-2">
                        <div className="space-y-1">
                          <Label className="text-sm">Changed since</Label>
                          <Input
                            type="datetime-local"
                            value={changedSince}
                            onChange={(e) => setChangedSince(e.target.value)}
                            className="h-9"
                          />
                          <p className="text-xs text-muted-foreground">Only export products with changes after this date</p>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-sm">Section filter</Label>
                          <Select value={changedSection} onValueChange={setChangedSection}>
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Any change" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_all">Any change</SelectItem>
                              <SelectItem value="summary">Summary (why_buy, full_description, differentiators)</SelectItem>
                              <SelectItem value="best_for">Best For</SelectItem>
                              <SelectItem value="buyer_profiles">Buyer Profiles</SelectItem>
                              <SelectItem value="use_cases">Use Cases</SelectItem>
                              <SelectItem value="benefits">Benefits</SelectItem>
                              <SelectItem value="faq">FAQ</SelectItem>
                              <SelectItem value="comparison">Comparison</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">Narrows <code>changed_since</code> to a single AEO section.</p>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="includeSectionVersions"
                            checked={includeSectionVersions}
                            onCheckedChange={(checked) => setIncludeSectionVersions(checked as boolean)}
                          />
                          <label htmlFor="includeSectionVersions" className="text-sm cursor-pointer">
                            Include per-column version hashes (+ aeo_content_hash rollup)
                          </label>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="hashesOnly"
                            checked={hashesOnly}
                            onCheckedChange={(checked) => setHashesOnly(checked as boolean)}
                          />
                          <label htmlFor="hashesOnly" className="text-sm cursor-pointer">
                            Hashes only (probe mode — no content)
                          </label>
                        </div>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={handleExportByCategory}
                    disabled={!selectedCategory || loading}
                    className="w-full"
                  >
                    {loading ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Export Enriched Feed
                  </Button>
                </CardContent>
              </Card>

              {/* Export by Question Wheel */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileJson className="h-5 w-5" />
                    Export by Question Wheel
                  </CardTitle>
                  <CardDescription>
                    Export products with their Q&A from a question wheel
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Question Wheel</Label>
                    <Select value={selectedWheel} onValueChange={setSelectedWheel}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a question wheel" />
                      </SelectTrigger>
                      <SelectContent>
                        {questionWheels.map((wheel) => (
                          <SelectItem key={wheel.id} value={wheel.id}>
                            {wheel.name} ({wheel.answered_count}/{wheel.question_count} answered)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Format</Label>
                    <Select value={format} onValueChange={(v) => setFormat(v as 'csv' | 'json')}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="csv">
                          <div className="flex items-center gap-2">
                            <FileSpreadsheet className="h-4 w-4" />
                            CSV (Spreadsheet)
                          </div>
                        </SelectItem>
                        <SelectItem value="json">
                          <div className="flex items-center gap-2">
                            <FileJson className="h-4 w-4" />
                            JSON (Structured)
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={handleExportByWheel}
                    disabled={!selectedWheel || loading}
                    className="w-full"
                  >
                    {loading ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Export Feed
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="api" className="space-y-4">
            {/* API Key Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  API Key
                </CardTitle>
                <CardDescription>
                  Use your API key to access the export endpoints programmatically
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <Input
                    type={showApiKey ? "text" : "password"}
                    value={apiKey || "No API key generated"}
                    readOnly
                    className="flex-1 font-mono"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(apiKey || "", "api-key")}
                    disabled={!apiKey}
                  >
                    {copiedId === "api-key" ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                {!apiKey && (
                  <Button onClick={generateApiKey} disabled={isGeneratingKey}>
                    {isGeneratingKey ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Key className="h-4 w-4 mr-2" />
                    )}
                    Generate API Key
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* API Endpoints Documentation */}
            <Card>
              <CardHeader>
                <CardTitle>Available Endpoints</CardTitle>
                <CardDescription>
                  Use these endpoints to export enriched product feeds programmatically
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Export by Category Endpoint */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">GET</Badge>
                    <span className="font-semibold">Export by Category</span>
                  </div>
                  <div className="bg-muted p-3 rounded-md font-mono text-sm break-all">
                    /api/sites/{'{siteId}'}/categories/{'{categoryName}'}/export/enriched-feed
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium mb-1">Query Parameters:</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li><code>format</code>: 'csv' | 'json' (default: csv)</li>
                      <li><code>useCanonicalNames</code>: 'true' | 'false' - Use standardized attribute names</li>
                      <li><code>includeDualFormat</code>: 'true' | 'false' - Include both original + canonical</li>
                      <li><code>includeEnrichments</code>: 'true' | 'false' - Include Q&A, benefit scores</li>
                      <li><code>includeProductQuestions</code>: 'true' | 'false' - Include product questions</li>
                    </ul>
                    <p className="font-medium mt-3 mb-1">AEO Content Parameters:</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li><code>includeCategoryAEO</code>: 'true' | 'false' - Include category AEO (featured snippets, buyer guides)</li>
                      <li><code>includeProductAEO</code>: 'true' | 'false' - Include product-level AEO content</li>
                      <li><code>includeAEOQuestions</code>: 'true' | 'false' - Include AEO FAQ questions & answers</li>
                      <li><code>aeoStatus</code>: 'approved' | 'any' - Filter by content approval status</li>
                      <li><code>includeReviewerInfo</code>: 'true' | 'false' - Include reviewer details for citations</li>
                      <li><code>aeoLanguage</code>: 'es' | 'en' | etc. - Language for AEO content (default: es). Use <code>all</code> to receive every available translation in one response — translatable AEO fields and per-row hashes become per-language maps; FAQs come back as structured arrays of <code>{'{question, answer, category}'}</code>. JSON only.</li>
                    </ul>

                    <p className="font-medium mt-3 mb-1">Filtering Parameters:</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li><code>filterByAeoStatus</code>: 'true' | 'false' - Only return products with AEO content matching aeoStatus</li>
                      <li><code>pendingDelivery</code>: 'true' | 'false' - Only products with AEO not yet acknowledged</li>
                      <li><code>pendingRepublish</code>: 'true' | 'false' - Only products needing (re)publication (never published OR updated since last publish)</li>
                      <li><code>faqFormat</code>: 'separate' | 'combined' - FAQ format (Q|Q|Q + A|A|A vs Q:A|Q:A)</li>
                    </ul>
                    <p className="font-medium mt-3 mb-1">Versioning / Change Detection Parameters:</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li><code>include_section_versions</code>: 'true' — append per-column SHA256 hashes + <code>updated_at</code> timestamps to each row under <code>section_versions</code>, keyed by the feed column name (e.g. <code>aeo_product_faqs</code>, <code>aeo_comparison_verdict</code>). Also adds a per-row <code>aeo_content_hash</code> rollup and <code>aeo_content_updated_at</code> (latest AEO regeneration across all columns) — store these per <code>item_group_id</code> to detect whether anything in the payload changed.</li>
                      <li><code>hashes_only</code>: 'true' — return the normal row schema with AEO content columns stripped, but with <code>section_versions</code>, <code>aeo_content_hash</code>, and <code>aeo_content_updated_at</code> present. Use for cheap polling: compare the stored <code>aeo_content_hash</code>; if different, inspect the per-column hashes to decide which fields to refetch. Always returns JSON.</li>
                      <li><code>changed_since</code>: ISO 8601 timestamp — only return products regenerated after this date (compared against the underlying AEO section versions).</li>
                      <li><code>changed_section</code>: Optional narrowing for <code>changed_since</code>. Accepts an internal section key: <code>summary</code>, <code>best_for</code>, <code>buyer_profiles</code>, <code>use_cases</code>, <code>benefits</code>, <code>faq</code>, or <code>comparison</code>. Defaults to <code>overall</code> (any change).</li>
                    </ul>
                  </div>

                  {/* Category selector for example */}
                  <div className="flex items-center gap-3 py-2">
                    <Label className="text-sm whitespace-nowrap">Example category:</Label>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger className="w-[300px]">
                        <SelectValue placeholder="Select a category to see example" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.name} value={cat.name}>
                            {cat.name} ({cat.product_count})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="bg-zinc-900 text-zinc-100 p-3 rounded-md text-sm font-mono">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-zinc-400">Example — full export:</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 text-zinc-400 hover:text-zinc-100"
                        onClick={() => copyToClipboard(
                          `curl -H "Authorization: Bearer ${apiKey || 'YOUR_API_KEY'}" \\
  "${baseUrl}/api/sites/${selectedSite?.id}/categories/${encodeURIComponent(selectedCategory || 'YOUR_CATEGORY')}/export/enriched-feed?format=json"`,
                          'curl-category'
                        )}
                      >
                        {copiedId === 'curl-category' ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <pre className="whitespace-pre-wrap">
{`curl -H "Authorization: Bearer ${apiKey || 'YOUR_API_KEY'}" \\
  "${baseUrl}/api/sites/${selectedSite?.id}/categories/${encodeURIComponent(selectedCategory || 'YOUR_CATEGORY')}/export/enriched-feed?format=json"`}
                    </pre>
                  </div>

                  <div className="bg-zinc-900 text-zinc-100 p-3 rounded-md text-sm font-mono">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-zinc-400">Example — incremental sync (only changed products):</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 text-zinc-400 hover:text-zinc-100"
                        onClick={() => copyToClipboard(
                          `curl -H "Authorization: Bearer ${apiKey || 'YOUR_API_KEY'}" \\
  "${baseUrl}/api/sites/${selectedSite?.id}/categories/${encodeURIComponent(selectedCategory || 'YOUR_CATEGORY')}/export/enriched-feed?format=json&filterByAeoStatus=true&includeProductAEO=true&changed_since=2026-04-01T00:00:00Z&include_section_versions=true"`,
                          'curl-category-versioning'
                        )}
                      >
                        {copiedId === 'curl-category-versioning' ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <pre className="whitespace-pre-wrap">
{`curl -H "Authorization: Bearer ${apiKey || 'YOUR_API_KEY'}" \\
  "${baseUrl}/api/sites/${selectedSite?.id}/categories/${encodeURIComponent(selectedCategory || 'YOUR_CATEGORY')}/export/enriched-feed?format=json&filterByAeoStatus=true&includeProductAEO=true&changed_since=2026-04-01T00:00:00Z&include_section_versions=true"`}
                    </pre>
                  </div>
                </div>

                {/* Pagination and response headers */}
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">Pagination &amp; Response Headers</span>
                  </div>
                  <div className="text-sm text-muted-foreground space-y-2">
                    <p>
                      The category export is <strong>cursor-paginated</strong>. Each response carries a{' '}
                      <code>Link</code> header with <code>rel=&quot;next&quot;</code> holding the URL of the next
                      page — request it unchanged to continue. <strong>When the response has no{' '}
                      <code>Link</code> header, the walk is complete.</strong> That remains the authoritative
                      end-of-feed signal.
                    </p>
                    <p className="font-medium mb-1">Response headers:</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li>
                        <code>X-PageMind-Page</code>: 1-based index of the page you are reading. Always present.
                      </li>
                      <li>
                        <code>X-PageMind-Total-Pages</code>: number of pages this walk will produce, computed when
                        the walk starts and carried through it unchanged. It is <strong>exact at the start of the
                        walk and advisory thereafter</strong> — the catalogue can change while you page through it,
                        so a walk that completed correctly can still end on a different page than the total
                        predicted. Use it with <code>X-PageMind-Page</code> for progress reporting, not as a
                        truncation check: if the page index reaches the total but a <code>Link</code> header is
                        still present, keep following it; if the <code>Link</code> header disappears before the
                        total is reached, the walk is complete. A page/total discrepancy on its own does not mean
                        the export was truncated. <strong>Omitted</strong> when the request sets{' '}
                        <code>pendingDelivery</code>, <code>pendingRepublish</code> or <code>changed_since</code>,
                        because counting the total under those filters is too slow to do on every walk. Its
                        absence is not an error.
                      </li>
                      <li>
                        <code>X-PageMind-Next-Since</code>: the watermark to pass as <code>changed_since</code> on
                        your next incremental run. Identical on every page of a walk.
                      </li>
                    </ul>
                    <p className="font-medium mb-1">Page size:</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li>
                        Defaults to <strong>50</strong>. Override with <code>pageSize</code> (or the legacy alias{' '}
                        <code>limit</code>).
                      </li>
                      <li>
                        Maximum is <strong>1000</strong> — except for multi-language AEO requests
                        (<code>includeProductAEO=true</code> together with <code>aeoLanguage=all</code>), where the
                        maximum is <strong>100</strong>, because a single row of that shape can reach ~486&nbsp;KB.
                        Values above the applicable maximum are <strong>silently clamped</strong>, not rejected — so
                        asking for 500 on a multi-language AEO export gives you pages of 100.
                      </li>
                      <li>
                        Do not change the page size part-way through a walk — the request is refused with{' '}
                        <code>400</code>, because a page total computed under one page size does not describe a walk
                        using another. Start a new walk (omit <code>cursor</code>) to change it.
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Export walk validation script */}
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">Validate a full export walk</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    This read-only script walks your export page by page using the cursor above and
                    reports whether the walk reached the end cleanly — HTTP status, the{' '}
                    <code>X-PageMind-Page</code> sequence, and whether{' '}
                    <code>X-PageMind-Total-Pages</code> stayed constant. It never marks anything as
                    delivered (<code>markAsFetched=false</code> on every request). Fill in your API key
                    and site ID (or export them as <code>PAGEMIND_API_KEY</code> /{' '}
                    <code>PAGEMIND_SITE_ID</code>) and run it.
                  </p>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="default" onClick={downloadWalkCheckScript}>
                      <Download className="h-4 w-4 mr-2" />
                      Download export-walk-check.sh
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(EXPORT_WALK_CHECK_SCRIPT, 'export-walk-check')}
                    >
                      {copiedId === 'export-walk-check' ? (
                        <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4 mr-2" />
                      )}
                      Copy to clipboard
                    </Button>
                  </div>

                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium mb-1">Run it from your terminal:</p>
                    {walkCheckExamples.map((example) => (
                      <div key={example.id} className="mb-3">
                        <div className="bg-zinc-900 text-zinc-100 p-3 rounded-md text-sm font-mono">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-zinc-400">{example.label}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 text-zinc-400 hover:text-zinc-100"
                              onClick={() => copyToClipboard(example.command, example.id)}
                            >
                              {copiedId === example.id ? (
                                <CheckCircle className="h-4 w-4" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                          <pre className="whitespace-pre-wrap">{example.command}</pre>
                        </div>
                        <p className="mt-1 text-xs">{example.note}</p>
                      </div>
                    ))}
                    <ul className="list-disc ml-4 space-y-1 mt-2">
                      <li><code>PAGEMIND_API_KEY</code> — your API key from the box above. Required.</li>
                      <li><code>PAGEMIND_SITE_ID</code> — the site to walk. Required.</li>
                      <li><code>MAX_PAGES</code> — stops after this many pages (default 10). Use <code>0</code> for a full walk.</li>
                      <li><code>PAGEMIND_BASE_URL</code> — defaults to <code>https://api.pagemind.ai</code>.</li>
                      <li>
                        <code>SAVE_DIR</code> — a directory to keep responses in. When set, the script saves{' '}
                        <strong>only the page that failed</strong>, as{' '}
                        <code>&lt;site_id&gt;_page0001.json</code> plus a matching{' '}
                        <code>.headers.txt</code>. Unset (the default) keeps nothing.
                      </li>
                      <li>
                        <code>SAVE_ALL=1</code> — with <code>SAVE_DIR</code>, keeps <em>every</em> page rather than
                        just the failure.
                      </li>
                    </ul>
                    <p className="mt-2">
                      <strong>Why the responses are not saved by default.</strong> These pages are large: with{' '}
                      <code>includeProductAEO</code> and <code>aeoLanguage=all</code>, three consecutive pages at
                      the default <code>pageSize=50</code> measured{' '}
                      <strong>8.9, 10.1 and 10.1&nbsp;MB</strong>, so a full walk of a few hundred pages runs to{' '}
                      <strong>gigabytes</strong>. <code>SAVE_DIR</code> on its own is the useful setting for
                      debugging — it costs one page and gives you the exact payload and headers that failed, which
                      is what to send us. Reach for <code>SAVE_ALL=1</code> only when you deliberately want the
                      whole feed on disk, and check you have room first.
                    </p>
                    <p className="mt-2">
                      Keeping the pages is also worth knowing about for a different reason: our own logs record
                      what we <em>sent</em> and how many rows, not the rows themselves. If you need to answer
                      later which products a given run delivered, the files from <code>SAVE_ALL</code> are the
                      only place that will say.
                    </p>
                    <p className="mt-2">
                      Needs <code>curl</code> and either <code>jq</code> or <code>python3</code>. Exits <code>0</code> when the
                      walk is clean, <code>1</code> when something is wrong — so you can run it from your own CI.
                    </p>
                  </div>

                  <details className="text-sm">
                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                      View the script source before running it ({EXPORT_WALK_CHECK_SCRIPT.split('\n').length} lines)
                    </summary>
                    <div className="bg-zinc-900 text-zinc-100 p-3 rounded-md text-xs font-mono mt-2 max-h-96 overflow-auto">
                      <pre className="whitespace-pre-wrap">{EXPORT_WALK_CHECK_SCRIPT}</pre>
                    </div>
                  </details>
                </div>

                {/* Export by Question Wheel Endpoint */}
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">POST</Badge>
                    <span className="font-semibold">Export by Question Wheel</span>
                  </div>
                  <div className="bg-muted p-3 rounded-md font-mono text-sm break-all">
                    /api/sites/{'{siteId}'}/question-wheels/{'{wheelId}'}/export-feed
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium mb-1">Request Body (JSON):</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li><code>format</code>: 'csv' | 'json' (default: csv)</li>
                      <li><code>includeStatus</code>: array of statuses ['approved', 'pending']</li>
                    </ul>
                  </div>

                  <div className="bg-zinc-900 text-zinc-100 p-3 rounded-md text-sm font-mono">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-zinc-400">Example:</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 text-zinc-400 hover:text-zinc-100"
                        onClick={() => copyToClipboard(
                          `curl -X POST -H "Authorization: Bearer ${apiKey || 'YOUR_API_KEY'}" \\
  -H "Content-Type: application/json" \\
  -d '{"format": "csv", "includeStatus": ["approved", "pending"]}' \\
  "${baseUrl}/api/sites/${selectedSite?.id}/question-wheels/WHEEL_ID/export-feed"`,
                          'curl-wheel'
                        )}
                      >
                        {copiedId === 'curl-wheel' ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <pre className="whitespace-pre-wrap">
{`curl -X POST -H "Authorization: Bearer ${apiKey || 'YOUR_API_KEY'}" \\
  -H "Content-Type: application/json" \\
  -d '{"format": "csv", "includeStatus": ["approved", "pending"]}' \\
  "${baseUrl}/api/sites/${selectedSite?.id}/question-wheels/WHEEL_ID/export-feed"`}
                    </pre>
                  </div>
                </div>

                {/* Response Fields */}
                <div className="space-y-3 pt-4 border-t">
                  <span className="font-semibold">Response Fields (per product)</span>
                  {/* webhook PRD rev. 5.3: the API, the bucket and the webhook point at one contract file */}
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">The body of each product is defined by this JSON schema.</p>
                    <SchemaDownloadButton siteId={selectedSite.id} />
                    {/* webhook PRD rev. 5.4: the exact request that returns the same product as a webhook call */}
                    <p className="text-sm text-muted-foreground break-all">{`The webhook sends each product in the same shape as this request returns it: ${matchingExportRequest(selectedSite.id)}`}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="space-y-1">
                      <p className="font-medium text-muted-foreground">Core Fields:</p>
                      <ul className="list-disc ml-4 space-y-1">
                        <li>id, sku, title, brand</li>
                        <li>category, price, currency</li>
                        <li>description, url</li>
                      </ul>
                    </div>
                    <div className="space-y-1">
                      <p className="font-medium text-muted-foreground">Enrichment Fields:</p>
                      <ul className="list-disc ml-4 space-y-1">
                        <li>qa_count - Total questions</li>
                        <li>qa_answered - Answered questions</li>
                        <li>top_questions - Top 5 questions</li>
                        <li>category_question_matches</li>
                      </ul>
                    </div>
                    <div className="space-y-1">
                      <p className="font-medium text-muted-foreground">AEO Category Fields:</p>
                      <ul className="list-disc ml-4 space-y-1">
                        <li>aeo_featured_snippet</li>
                        <li>aeo_category_purpose</li>
                        <li>aeo_buyer_guide_intro</li>
                        <li>aeo_narrative</li>
                        <li>aeo_category_status</li>
                      </ul>
                    </div>
                    <div className="space-y-1">
                      <p className="font-medium text-muted-foreground">AEO FAQ Fields:</p>
                      <ul className="list-disc ml-4 space-y-1">
                        <li>aeo_faq_questions</li>
                        <li>aeo_faq_answers</li>
                        <li>aeo_faq_count</li>
                        <li>aeo_approved_at</li>
                        <li>aeo_approved_by</li>
                      </ul>
                    </div>
                    <div className="space-y-1">
                      <p className="font-medium text-muted-foreground">Product AEO Fields:</p>
                      <ul className="list-disc ml-4 space-y-1">
                        <li>aeo_product_why_buy</li>
                        <li>aeo_product_best_for</li>
                        <li>aeo_product_differentiators</li>
                        <li>aeo_product_faqs</li>
                        <li>aeo_product_status</li>
                        <li>aeo_product_approved_at</li>
                        <li>aeo_product_reviewer</li>
                        <li>aeo_product_last_published_at</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
